import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = '/api';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '2px solid #e5e7eb',
  fontSize: 15,
  outline: 'none',
  transition: 'border 0.2s',
  fontFamily: "'Heebo', sans-serif",
  boxSizing: 'border-box',
};

const readonlyInputStyle: React.CSSProperties = {
  ...inputStyle,
  background: '#f9fafb',
  color: '#6b7280',
  cursor: 'default',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const ExternalRegisterPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [leadData, setLeadData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [terminalMasof, setTerminalMasof] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [terminalPassword, setTerminalPassword] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [section46, setSection46] = useState(false);
  const [logoBase64, setLogoBase64] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [receiptStartNumber, setReceiptStartNumber] = useState(1);
  const [creditNoteStartNumber, setCreditNoteStartNumber] = useState(1);
  const [taxReceiptStartNumber, setTaxReceiptStartNumber] = useState(1);
  const [websiteUrl, setWebsiteUrl] = useState('');

  const handleLogoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoBase64(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#7c3aed';
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#e5e7eb';
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}/leads/by-token/${token}`)
      .then(res => setLeadData(res.data))
      .catch(err => setError(err?.response?.data?.error || 'לינק לא תקף'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('סיסמה חייבת להכיל 8 תווים לפחות');
      return;
    }
    if (password !== confirmPassword) {
      setError('הסיסמאות לא תואמות');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/register-from-lead`, {
        token,
        password,
        terminalMasof,
        apiKey,
        terminalPassword,
        nameEn,
        receiptStartNumber,
        creditNoteStartNumber,
        taxReceiptStartNumber,
        websiteUrl,
        section46,
        ...(logoBase64 ? { logo: logoBase64 } : {}),
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      window.location.href = '/';
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה בהרשמה');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        dir="rtl"
        style={{
          minHeight: '100vh',
          background: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Heebo', sans-serif",
        }}
      >
        <div style={{ textAlign: 'center', color: '#6b7280' }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: '4px solid #e5e7eb',
              borderTopColor: '#7c3aed',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontSize: 16 }}>טוען נתונים...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid token)
  if (error && !leadData) {
    return (
      <div
        dir="rtl"
        style={{
          minHeight: '100vh',
          background: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Heebo', sans-serif",
        }}
      >
        <div style={{ width: '100%', maxWidth: 480, padding: 24 }}>
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                padding: '32px 24px',
                color: '#fff',
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 8 }}>{error?.includes('כבר נרשמה') ? '✅' : '⚠️'}</div>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>
                {error?.includes('כבר נרשמה') ? 'העמותה כבר נרשמה' : 'לינק לא תקף'}
              </h1>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7 }}>
                {error?.includes('כבר נרשמה') ? 'ניתן להתחבר למערכת עם הפרטים שהזנתם בהרשמה.' : error}
              </p>
              {error?.includes('כבר נרשמה') ? (
                <a
                  href="/login"
                  style={{
                    display: 'inline-block',
                    marginTop: 12,
                    padding: '10px 24px',
                    background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                    color: '#fff',
                    borderRadius: 12,
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  התחבר למערכת
                </a>
              ) : (
              <a
                href="/contact"
                style={{
                  display: 'inline-block',
                  marginTop: 12,
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  color: '#fff',
                  borderRadius: 12,
                  textDecoration: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                צרו קשר
              </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        background: '#f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Heebo', sans-serif",
        padding: '16px 0',
      }}
    >
      <div style={{ width: '100%', maxWidth: 600, padding: '0 12px' }}>
        <div
          style={{
            background: '#fff',
            borderRadius: 24,
            boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
              padding: '32px 24px',
              textAlign: 'center',
              color: '#fff',
            }}
          >
            <div
              style={{
                fontSize: 28,
                fontWeight: 900,
                letterSpacing: 1,
                marginBottom: 4,
              }}
            >
              Amutech
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: '8px 0 0' }}>
              השלמת הרשמה — {leadData?.orgName || ''}
            </h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
            {/* Readonly pre-filled fields */}
            <h3
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: '#7c3aed',
                marginBottom: 14,
                marginTop: 0,
              }}
            >
              פרטים שנקלטו
            </h3>
            <div
              className="grid grid-cols-1 sm:grid-cols-2"
              style={{
                gap: 12,
                marginBottom: 20,
              }}
            >
              <div>
                <label style={labelStyle}>שם עמותה</label>
                <input
                  type="text"
                  value={leadData?.orgName || ''}
                  readOnly
                  style={readonlyInputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>מספר רישום</label>
                <input
                  type="text"
                  value={leadData?.registrationNumber || ''}
                  readOnly
                  style={readonlyInputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>שם איש קשר</label>
                <input
                  type="text"
                  value={leadData?.contactName || ''}
                  readOnly
                  style={readonlyInputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>אימייל</label>
                <input
                  type="email"
                  value={leadData?.contactEmail || ''}
                  readOnly
                  style={readonlyInputStyle}
                  dir="ltr"
                />
              </div>
            </div>

            {/* Password fields */}
            <h3
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: '#7c3aed',
                marginBottom: 14,
              }}
            >
              הגדרת סיסמה
            </h3>
            <div
              className="grid grid-cols-1 sm:grid-cols-2"
              style={{
                gap: 12,
                marginBottom: 20,
              }}
            >
              <div>
                <label style={labelStyle}>סיסמה *</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="לפחות 8 תווים"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <label style={labelStyle}>אישור סיסמה *</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="הקלידו שוב"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </div>
            </div>

            {/* Collapsible payment section */}
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 14,
                marginBottom: 20,
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                onClick={() => setShowPayment(!showPayment)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: '#f9fafb',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontFamily: "'Heebo', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                <span>הגדרות סליקה (לא חובה)</span>
                <span
                  style={{
                    transform: showPayment ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    fontSize: 12,
                  }}
                >
                  &#9660;
                </span>
              </button>
              {showPayment && (
                <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <p
                    style={{
                      fontSize: 13,
                      color: '#9ca3af',
                      marginTop: 0,
                      marginBottom: 14,
                    }}
                  >
                    ניתן להגדיר מאוחר יותר בהגדרות המערכת
                  </p>
                  <div
                    className="grid grid-cols-1 sm:grid-cols-3"
                    style={{
                      gap: 12,
                    }}
                  >
                    <div>
                      <label style={labelStyle}>מספר מסוף</label>
                      <input
                        type="text"
                        value={terminalMasof}
                        onChange={e => setTerminalMasof(e.target.value)}
                        placeholder="Masof"
                        style={inputStyle}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>API Key</label>
                      <input
                        type="text"
                        value={apiKey}
                        onChange={e => setApiKey(e.target.value)}
                        placeholder="API Key"
                        style={inputStyle}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>סיסמת מסוף</label>
                      <input
                        type="password"
                        value={terminalPassword}
                        onChange={e => setTerminalPassword(e.target.value)}
                        placeholder="Password"
                        style={inputStyle}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Collapsible extra details section */}
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 14,
                marginBottom: 20,
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                onClick={() => setShowExtra(!showExtra)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: '#f9fafb',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontFamily: "'Heebo', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                <span>פרטים נוספים (לא חובה)</span>
                <span
                  style={{
                    transform: showExtra ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    fontSize: 12,
                  }}
                >
                  &#9660;
                </span>
              </button>
              {showExtra && (
                <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Section 46 checkbox */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
                    <input
                      type="checkbox"
                      id="section46"
                      checked={section46}
                      onChange={e => setSection46(e.target.checked)}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                    <label htmlFor="section46" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>
                      יש אישור לסעיף 46 (פטור ממס לתורמים)
                    </label>
                  </div>

                  {/* Logo upload */}
                  <div>
                    <label style={labelStyle}>לוגו העמותה (קובץ תמונה)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFile}
                      style={{ ...inputStyle, padding: '8px 12px', cursor: 'pointer' }}
                    />
                    {logoBase64 && (
                      <img src={logoBase64} alt="לוגו" style={{ marginTop: 8, height: 48, objectFit: 'contain', borderRadius: 8 }} />
                    )}
                  </div>

                  {/* org name English */}
                  <div>
                    <label style={labelStyle}>שם העמותה באנגלית</label>
                    <input type="text" value={nameEn} onChange={e => setNameEn(e.target.value)}
                      placeholder="Organization Name" style={{...inputStyle, direction: 'ltr'}}
                      onFocus={handleFocus} onBlur={handleBlur} />
                  </div>

                  {/* Receipt start number */}
                  <div>
                    <label style={labelStyle}>מספר התחלה — סדרת קבלות</label>
                    <input type="number" value={receiptStartNumber} min={1}
                      onChange={e => setReceiptStartNumber(Number(e.target.value))}
                      style={{...inputStyle, direction: 'ltr'}}
                      onFocus={handleFocus} onBlur={handleBlur} />
                  </div>

                  {/* Credit note start number */}
                  <div>
                    <label style={labelStyle}>מספר התחלה — חשבוניות זיכוי</label>
                    <input type="number" value={creditNoteStartNumber} min={1}
                      onChange={e => setCreditNoteStartNumber(Number(e.target.value))}
                      style={{...inputStyle, direction: 'ltr'}}
                      onFocus={handleFocus} onBlur={handleBlur} />
                  </div>

                  {/* Tax receipt start number */}
                  <div>
                    <label style={labelStyle}>מספר התחלה — חשבונית מס קבלה</label>
                    <input type="number" value={taxReceiptStartNumber} min={1}
                      onChange={e => setTaxReceiptStartNumber(Number(e.target.value))}
                      style={{...inputStyle, direction: 'ltr'}}
                      onFocus={handleFocus} onBlur={handleBlur} />
                  </div>

                  {/* Website URL */}
                  <div>
                    <label style={labelStyle}>קישור לאתר (אופציונלי)</label>
                    <input type="url" value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.target.value)}
                      placeholder="https://www.amuta.org.il"
                      style={{...inputStyle, direction: 'ltr'}}
                      onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div
                style={{
                  background: '#fef2f2',
                  color: '#dc2626',
                  padding: '10px 16px',
                  borderRadius: 10,
                  fontSize: 14,
                  marginBottom: 16,
                  textAlign: 'center',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '14px',
                background: submitting
                  ? '#d1d5db'
                  : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                color: '#fff',
                border: 'none',
                borderRadius: 14,
                fontSize: 17,
                fontWeight: 800,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: "'Heebo', sans-serif",
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: submitting
                  ? 'none'
                  : '0 4px 14px rgba(124,58,237,0.3)',
              }}
              onMouseEnter={e => {
                if (!submitting) e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {submitting ? 'נרשם...' : 'הצטרף ל-Amutech'}
            </button>
          </form>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              background: '#f9fafb',
              borderTop: '1px solid #f3f4f6',
              textAlign: 'center',
              fontSize: 13,
              color: '#9ca3af',
            }}
          >
            Amutech — מערכת ניהול לעמותות
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExternalRegisterPage;
